import { Combobox } from '@headlessui/react';
import { useState, useEffect, Fragment } from 'react';

import CheckIcon from '@common/assets/icons/check.svg?react';
import PickerIcon from '@common/assets/icons/picker.svg?react';
import SearchIcon from '@common/assets/icons/search.svg?react';
import { Card } from '@common/ui/Card';
import { Cell } from '@common/ui/Cell';
import { Modal } from '@common/ui/Modal';
import { Result } from '@common/ui/Result';
import { Separator } from '@common/ui/Separator';

import styles from './AutoComplete.module.scss';
import { Input, type InputProps } from '../Input';

export type Item = {
  value: string;
  title: string;
  subtitle?: string;
};

export type AutoCompleteProps = Omit<
  InputProps,
  'onSelect' | 'onChange' | 'multiple'
> & {
  value?: string;
  items?: Item[];
  onSelect?: (value: string) => void;
  gap?: number;
  searchPlaceholder?: string;
};

export const AutoComplete: React.FC<AutoCompleteProps> = ({
  items,
  onSelect,
  gap,
  searchPlaceholder,
  ...props
}) => {
  const [query, setQuery] = useState<string>('');
  const [selected, setSelected] = useState<string | null>(props.value ?? null);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  useEffect(() => {
    setSelected(props.value ?? null);
  }, [props.value]);

  const onSelectHandler = (value: string) => {
    setSelected(value);
    onSelect?.(value);
    onCloseHandler();
  };

  const onOpenHandler = () => {
    setIsOpen(true);
  };

  const onCloseHandler = () => {
    setIsOpen(false);
    props.onBlur?.({} as React.FocusEvent<HTMLInputElement>);
  };

  const filteredItems =
    query === ''
      ? items || []
      : items?.filter((item) =>
          item.title.toLowerCase().includes(query.toLowerCase()),
        ) || [];

  return (
    <Combobox value={selected}>
      <div
        className={styles.autoComplete}
        data-test-id="auto-complete-container"
      >
        <div
          className={styles.inputWrapper}
          onClick={onOpenHandler}
          data-test-id="auto-complete-trigger"
        >
          <Input
            {...props}
            data-test-id="auto-complete-input"
            value={
              items?.find((item) => selected === item.value)?.title ??
              selected ??
              ''
            }
            onChange={(e) => setQuery(e.target.value)}
            onFocus={(e) => e.target.blur()}
            suffixIcon={<PickerIcon />}
            style={{ ...props.style, pointerEvents: 'none' }}
          />
        </div>

        <Modal
          testId="auto-complete-modal"
          className={styles.modal}
          open={isOpen}
          onClose={onCloseHandler}
        >
          <div className={styles.content}>
            <Input
              data-test-id="auto-complete-search-input"
              placeholder={searchPlaceholder}
              prefixIcon={<SearchIcon />}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
            <Card
              testId="auto-complete-options"
              className={styles.card}
              style={{ gridGap: gap, padding: '4px 16px' }}
            >
              {filteredItems.map((item, index) => (
                <Fragment key={index}>
                  <Cell
                    testId={`auto-complete-option-${item.value}`}
                    title={item.title}
                    subtitle={item.subtitle}
                    suffixIcon={
                      item.value === selected && (
                        <CheckIcon className={styles.icon} />
                      )
                    }
                    onClick={() => onSelectHandler(item.value)}
                  />
                  {index < filteredItems.length - 1 && <Separator />}
                </Fragment>
              ))}
              {filteredItems.length === 0 && (
                <Result
                  src={'/icons/3d-icons/not-found.png'}
                  title="Ничего не нашли"
                  description="Попробуйте изменить запрос"
                  style={{ padding: '16px 0' }}
                />
              )}
            </Card>
          </div>
        </Modal>
      </div>
    </Combobox>
  );
};

AutoComplete.displayName = 'AutoComplete';
